import type { InstagramAppSettingsDto } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Field, Input, PasswordInput, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { ChannelIcon } from '../components/channel-icon.js';
import { useEffect, useState } from 'react';
import { fieldErrors } from '../clinical/patients-page.js';
import { api } from '../lib/api.js';

const igSettingsQuery = queryOptions({ queryKey: ['platform', 'settings', 'instagram'], queryFn: () => api<InstagramAppSettingsDto>('/platform/settings/instagram') });

function CopyRow({ label, value }: { label: string; value: string }) {
  const [done, setDone] = useState(false);
  return (
    <>
      <dt className="text-fg-muted">{label}</dt>
      <dd className="flex items-center gap-2 font-mono break-all text-fg">
        <span className="min-w-0 flex-1">{value}</span>
        <button type="button" aria-label={`Copiar ${label}`} onClick={() => { void navigator.clipboard.writeText(value).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }); }}
          className="grid size-7 shrink-0 place-items-center rounded-md text-fg-subtle hover:bg-surface hover:text-fg">{done ? '✓' : <Copy className="size-3.5" />}</button>
      </dd>
    </>
  );
}

/** Console → Configurações → Instagram: app da Meta (produto Instagram, login do Instagram), vale para todas as clínicas. */
export function InstagramSettingsCard() {
  const qc = useQueryClient();
  const { data } = useQuery(igSettingsQuery);
  const [appId, setAppId] = useState('');
  const [secret, setSecret] = useState('');
  useEffect(() => { if (data?.appId) setAppId(data.appId); }, [data?.appId]);
  const save = useMutation({
    mutationFn: () => api<InstagramAppSettingsDto>('/platform/settings/instagram', { method: 'PUT', body: { appId, ...(secret.trim() ? { appSecret: secret.trim() } : {}) } }),
    onSuccess: (r) => { qc.setQueryData(igSettingsQuery.queryKey, r); setSecret(''); },
  });
  const errs = fieldErrors(save.error);
  if (!data) return <Card className="mt-4 grid h-32 place-items-center"><Spinner className="size-5 text-fg-subtle" label="Carregando" /></Card>;
  return (
    <Card className="mt-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-fg"><ChannelIcon kind="instagram" className="text-fg-subtle" />Instagram Direct (app da Meta)</h2>
          <p className="mt-1 text-[13px] text-fg-muted">
            No Meta for Developers: app do tipo Empresa → produto Instagram → “API com login do Instagram”. Copie o ID e a chave secreta do app do Instagram,
            cadastre a URL de redirecionamento e o webhook abaixo (campo <span className="font-mono">messages</span>). Depois, cada clínica conecta a conta em Configurações › Canais.
          </p>
        </div>
        {data.configured ? <Badge tone="success" className="shrink-0">Configurado</Badge> : <Badge tone="warning" className="shrink-0 whitespace-nowrap">Não configurado</Badge>}
      </div>
      <dl className="mt-4 grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-1.5 rounded-lg bg-surface-muted p-3.5 text-[13px]">
        <CopyRow label="URL de redirecionamento" value={data.redirectUri} />
        <CopyRow label="URL do webhook" value={data.webhookUrl} />
        {data.verifyToken && <CopyRow label="Token de verificação" value={data.verifyToken} />}
      </dl>
      <form className="mt-4 grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="ID do app do Instagram" error={errs.appId}><Input value={appId} onChange={(e) => setAppId(e.target.value)} inputMode="numeric" /></Field>
        <Field label="Chave secreta do app" error={errs.appSecret} hint={data.secretSet ? 'Já salva. Preencha só para trocar.' : undefined}>
          <PasswordInput value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="off" />
        </Field>
        <Button type="submit" disabled={!appId.trim()} loading={save.isPending}>Salvar</Button>
      </form>
      {save.error && !Object.keys(errs).length && <Alert tone="danger" className="mt-3">{save.error.message}</Alert>}
    </Card>
  );
}
